/**
 * BulkUpload Component
 * 
 * Handles bulk upload of initiatives from CSV/Excel files.
 * Provides preview, validation, duplicate detection, and import functionality.
 * 
 * Features:
 * - Drag & drop file upload
 * - CSV parsing and validation
 * - Preview with error highlighting
 * - Duplicate detection
 * - Selective import
 * 
 * @module components/BulkUpload
 */

// Components use EventBus pattern - no direct imports needed
import { openOverlay, closeOverlay } from '../utils/Overlay.js';
import { createBulkUploadContent, bindBulkUploadHandlers } from './bulk-upload/BulkUploadView.js';
import BulkUploadController from './bulk-upload/BulkUploadController.js';

export class BulkUpload {
  constructor() {
    this.isOpen = false;
    this._controller = null;
  }
  
  /**
   * Open the bulk upload modal
   */
  open() {
    this.isOpen = true;
    const content = this.createUploadContent();
    // Use simple overlay to avoid modal flash
    this._overlayId = openOverlay({
      title: '🤖 AI-Enhanced Bulk Upload',
      content: content,
      actions: [ { label: 'Cancel', class: 'btn-ghost', handler: () => this.close() } ]
    });

    // Deterministic bind after content mount
    requestAnimationFrame(() => {
      try {
        this._controller = new BulkUploadController(this);
        bindBulkUploadHandlers({
          onFileSelected: (file) => this._controller.onFileSelected(file),
          onToggleAllRows: (checked) => this._controller.onToggleAllRows(checked),
          onCheckDuplicates: () => this._controller.onCheckDuplicates(),
          onConfirmImport: () => this._controller.onConfirmImport(),
          onClose: () => this._controller.onClose()
        });
      } catch (e) {
        console.warn('BulkUpload binding error:', e);
      }
    });
  }
  
  /**
   * Create upload interface content
   */
  createUploadContent() {
    return createBulkUploadContent();
  }
  
  /**
   * Escape HTML for safe display
   */
  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }
  
  /**
   * Close the modal
   */
  close() {
    this.isOpen = false;
    this._controller = null;
    if (this._overlayId) closeOverlay(this._overlayId);
  }
}

// Create singleton instance
export const bulkUpload = new BulkUpload();

// Attach to window for global access
if (import.meta.env.DEV) {
  window.bulkUpload = bulkUpload;
  window.BulkUpload = BulkUpload;
}

// Add styles
const styles = `
<style id="bulk-upload-styles">
.bulk-upload-modal .modal-content {
  max-width: 1000px;
  max-height: 80vh;
  overflow-y: auto;
}

.bulk-upload-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.upload-step {
  padding: 1.5rem;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.upload-step h4 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
}

.upload-drop-zone {
  border: 2px dashed var(--border-color);
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  transition: all 0.3s;
  position: relative;
}

.upload-drop-zone:hover,
.upload-drop-zone.drag-over {
  border-color: var(--primary-color);
  background: var(--primary-light);
}

.drop-zone-content,
.drop-zone-active {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.drop-zone-icon {
  font-size: 3rem;
}

.supported-formats {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 1rem;
}

.upload-info {
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 8px;
  text-align: center;
}

.upload-info p {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-primary);
}

.preview-stats {
  margin-bottom: 1rem;
}

.stat-badges {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.badge-info {
  background: var(--info-bg);
  color: var(--info-color);
}

.badge-success {
  background: var(--success-bg);
  color: var(--success-color);
}

.badge-danger {
  background: var(--error-bg);
  color: var(--error-color);
}

.badge-warning {
  background: rgba(255, 193, 7, 0.2);
  color: var(--color-warning);
}

.preview-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 6px;
}

.preview-table-container {
  overflow-x: auto;
  border: 1px solid var(--border-color);
  border-radius: 6px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
}

.preview-table thead {
  background: var(--bg-primary);
  border-bottom: 2px solid var(--border-color);
}

.preview-table th {
  padding: 0.75rem;
  text-align: left;
  font-weight: 500;
  color: var(--text-primary);
}

.preview-table td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.preview-row:hover {
  background: var(--bg-hover);
}

.preview-row.row-error {
  background: var(--error-bg);
}

.truncate {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.priority-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.priority-low {
  background: #e3f2fd;
  color: #1976d2;
}

.priority-medium {
  background: #fff3e0;
  color: #f57c00;
}

.priority-high {
  background: #fbe9e7;
  color: #d84315;
}

.priority-critical {
  background: #ffebee;
  color: #c62828;
}

.validation-errors {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--error-bg);
  border: 1px solid var(--error-color);
  border-radius: 6px;
}

.validation-errors h5 {
  margin: 0 0 0.5rem 0;
  color: var(--error-color);
}

.error-list {
  max-height: 150px;
  overflow-y: auto;
}

.error-item {
  padding: 0.25rem 0;
  font-size: 0.875rem;
}

.duplicate-stats {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.duplicate-list {
  max-height: 300px;
  overflow-y: auto;
}

.duplicate-item {
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.duplicate-exact {
  background: var(--error-bg);
  border-color: var(--error-color);
}

.duplicate-similar {
  background: rgba(255, 193, 7, 0.1);
  border-color: var(--color-warning);
}

.duplicate-type {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.duplicate-compare {
  margin: 0.5rem 0;
  font-size: 0.875rem;
}

.duplicate-new,
.duplicate-existing {
  padding: 0.25rem 0;
}

.import-ready {
  padding: 1.5rem;
  background: var(--success-bg);
  border: 2px solid var(--success-color);
  border-radius: 8px;
  text-align: center;
}

.import-count {
  font-size: 2rem;
  font-weight: bold;
  color: var(--success-color);
  margin: 1rem 0;
}

.import-preview {
  text-align: left;
  margin: 1rem auto;
  max-width: 500px;
}

.import-preview ul {
  margin: 0;
  padding-left: 1.5rem;
}

.import-actions,
.completion-actions {
  margin-top: 2rem;
  text-align: center;
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1.125rem;
}

.upload-progress {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--bg-primary);
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  min-width: 300px;
}

.upload-progress-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  z-index: 9999;
}

.progress-bar {
  height: 20px;
  background: var(--bg-secondary);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s;
}

.progress-text {
  text-align: center;
  color: var(--text-primary);
}

.success-message {
  text-align: center;
  color: var(--success-color);
  font-size: 1.125rem;
  padding: 2rem;
}

.badge-ai {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

.badge-ai-detail {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  font-size: 0.75rem;
}

.ai-summary {
  margin-top: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 8px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-primary);
}

/* Completion Step Styles */
.completion-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;
  flex-wrap: wrap;
}

.stat-big {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 12px;
  min-width: 200px;
}

.stat-big.stat-success {
  background: var(--success-bg);
  border: 2px solid var(--success-color);
}

.stat-big.stat-error {
  background: var(--error-bg);
  border: 2px solid var(--error-color);
}

.stat-icon {
  font-size: 2.5rem;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  line-height: 1;
}

.stat-big.stat-success .stat-number {
  color: var(--success-color);
}

.stat-big.stat-error .stat-number {
  color: var(--error-color);
}

.stat-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.completion-message {
  margin: 2rem 0;
  text-align: center;
}

.message-success,
.message-warning,
.message-error {
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1rem 0;
}

.message-success {
  background: var(--success-bg);
  border: 1px solid var(--success-color);
}

.message-warning {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid var(--color-warning);
}

.message-error {
  background: var(--error-bg);
  border: 1px solid var(--error-color);
}

.message-success h5 {
  color: var(--success-color);
}

.message-warning h5 {
  color: var(--color-warning);
}

.message-error h5 {
  color: var(--error-color);
}

.next-steps {
  margin: 2rem 0;
  padding: 1.5rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  text-align: left;
}

.next-steps h6 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
}

.next-steps ul {
  margin: 0;
  padding-left: 1.5rem;
}

.next-steps li {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

/* Dark mode adjustments */
.dark-mode .upload-step {
  background: #1e1e1e;
}

.dark-mode .preview-table thead {
  background: #2a2a2a;
}

.dark-mode .preview-controls {
  background: #2a2a2a;
}

/* Responsive */
@media (max-width: 768px) {
  .bulk-upload-modal .modal-content {
    max-width: 95%;
  }
  
  .preview-table-container {
    font-size: 0.875rem;
  }
  
  .truncate {
    max-width: 100px;
  }
  
  .completion-stats {
    flex-direction: column;
    align-items: center;
  }
  
  .stat-big {
    min-width: auto;
    width: 100%;
    max-width: 300px;
  }
}
</style>
`;

// Inject styles if not already present
if (!document.getElementById('bulk-upload-styles')) {
  document.head.insertAdjacentHTML('beforeend', styles);
}
